import React from 'react';
import {View, StyleSheet, TouchableOpacity} from 'react-native';
import CCText from '@/Atoms/CCText';
import Spacer from '@/Atoms/Spacer';
import {vs} from '@/Utils/Dimensions';
import Ionicons from 'react-native-vector-icons/Ionicons';
import {EligibilityCriterion} from './types/registrationTypes';

interface NotEligibleScreenProps {
  criteria: EligibilityCriterion[];
  errorMessage: string;
  onClose: () => void;
  onSeeAllTournaments: () => void;
  onBuyMembership?: () => void;
}

const CriterionRow = ({item}: {item: EligibilityCriterion}) => {
  const failPillText = !item.passed && item.hint ? item.hint : 'FAILED';

  return (
    <View style={[styles.row, !item.passed && styles.rowFail]}>
      <View style={styles.rowIcon}>
        {item.passed ? (
          <Ionicons name="checkmark-circle-outline" size={20} color="#3DAB9E" />
        ) : (
          <Ionicons name="close-circle-outline" size={20} color="#CD2C39" />
        )}
      </View>
      <View style={styles.rowText}>
        <CCText fontSize={13} fontWeight="semibold" color="#262322">
          {item.label}
        </CCText>
        <CCText fontSize={12} color="#888280" style={styles.rowValue}>
          {item.value}
        </CCText>
        {item.hint && !item.passed && (
          <CCText
            fontSize={11}
            color="#CD2C39"
            fontWeight="medium"
            style={styles.rowHint}>
            {item.hint}
          </CCText>
        )}
      </View>
      <View
        style={[styles.pill, item.passed ? styles.pillPass : styles.pillFail]}>
        <CCText
          fontSize={10}
          fontWeight="bold"
          color={item.passed ? '#4B968D' : '#CD2C39'}
          style={styles.pillLabel}>
          {item.passed ? 'MEETS' : failPillText}
        </CCText>
      </View>
    </View>
  );
};

export default function NotEligibleScreen({
  criteria,
  errorMessage,
  onClose: _onClose,
  onSeeAllTournaments,
  onBuyMembership,
}: NotEligibleScreenProps) {
  const failedCount = criteria.filter(c => !c.passed).length;
  const totalCount = criteria.length;

  const isMembershipFailure =
    errorMessage?.toLowerCase()?.includes('membership') ||
    errorMessage?.toLowerCase()?.includes('not a member');

  return (
    <View>
      {/* Headline card */}
      <View style={styles.headline}>
        <View style={styles.headlineIcon}>
          <Ionicons name="close-circle" size={22} color="#CD2C39" />
        </View>
        <View style={styles.headlineTextWrap}>
          <CCText fontSize={16} fontWeight="semibold" color="#262322">
            {failedCount} of {totalCount} criteria didn't match
          </CCText>
          <Spacer spacing={vs(4)} />
          <CCText fontSize={13} color="#4D3F37" style={styles.headlineDesc}>
            {errorMessage}
          </CCText>
        </View>
      </View>

      {/* Criteria list */}
      <View style={styles.list}>
        <CCText
          fontSize={11}
          fontWeight="bold"
          color="#888280"
          style={styles.sectionLabel}>
          ELIGIBILITY CRITERIA
        </CCText>
        <Spacer spacing={vs(10)} />
        {criteria.map((item, index) => (
          <React.Fragment key={item.criterion}>
            <CriterionRow item={item} />
            {index < criteria.length - 1 && <Spacer spacing={vs(6)} />}
          </React.Fragment>
        ))}
      </View>

      {/* Suggested next step card */}
      <View style={styles.ctaCard}>
        <View style={styles.ctaCardText}>
          <CCText
            fontSize={10}
            fontWeight="bold"
            color="#806234"
            style={styles.ctaCardLabel}>
            SUGGESTED NEXT STEP
          </CCText>
          <CCText fontSize={14} fontWeight="semibold" color="#262322">
            Play rated games
          </CCText>
          <CCText fontSize={12} color="#888280">
            Improve your rating to meet the criteria
          </CCText>
        </View>
        <TouchableOpacity
          style={styles.ctaSmall}
          activeOpacity={0.7}
          onPress={onSeeAllTournaments}>
          <CCText fontSize={13} fontWeight="bold" color="#262322">
            Play now
          </CCText>
        </TouchableOpacity>
      </View>

      {/* Footer */}
      <View style={styles.foot}>
        {isMembershipFailure && onBuyMembership && (
          <TouchableOpacity
            style={styles.primaryButton}
            onPress={onBuyMembership}
            activeOpacity={0.7}>
            <CCText fontSize={13} fontWeight="bold" color="#FFFFFF">
              Buy Membership
            </CCText>
          </TouchableOpacity>
        )}
        <TouchableOpacity
          style={styles.ghostButton}
          onPress={onSeeAllTournaments}
          activeOpacity={0.7}>
          <CCText fontSize={13} fontWeight="semibold" color="#262322">
            See all eligible tournaments
          </CCText>
        </TouchableOpacity>
        <View style={styles.help}>
          <CCText fontSize={11} color="#888280">
            Think this is a mistake?{' '}
          </CCText>
          <TouchableOpacity activeOpacity={0.7}>
            <CCText fontSize={11} color="#4B968D" fontWeight="semibold">
              Contact organisers
            </CCText>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  /* Headline card */
  headline: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 14,
    marginTop: 18,
    padding: 16,
    backgroundColor: 'rgba(205, 44, 57, 0.05)',
    borderWidth: 1,
    borderColor: 'rgba(205, 44, 57, 0.18)',
    borderRadius: 14,
  },
  headlineIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headlineTextWrap: {
    flex: 1,
  },
  headlineDesc: {
    lineHeight: 20,
  },

  /* Section label + list */
  list: {
    marginTop: 22,
  },
  sectionLabel: {
    letterSpacing: 0.88,
    textTransform: 'uppercase',
  },

  /* Criteria rows */
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: 'rgba(38, 35, 34, 0.10)',
    borderRadius: 12,
  },
  rowFail: {
    backgroundColor: 'rgba(205, 44, 57, 0.04)',
    borderColor: 'rgba(205, 44, 57, 0.18)',
  },
  rowIcon: {
    marginTop: 1,
  },
  rowText: {
    flex: 1,
  },
  rowValue: {
    marginTop: 1,
  },
  rowHint: {
    marginTop: 4,
  },

  /* Pills */
  pill: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    alignSelf: 'center',
  },
  pillPass: {
    backgroundColor: '#E4F6EB',
  },
  pillFail: {
    backgroundColor: 'rgba(205, 44, 57, 0.12)',
  },
  pillLabel: {
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },

  /* Suggested next step card */
  ctaCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    marginTop: 20,
    paddingVertical: 14,
    paddingHorizontal: 16,
    backgroundColor: '#FAF6EB',
    borderWidth: 1,
    borderColor: 'rgba(250, 207, 71, 0.5)',
    borderRadius: 14,
  },
  ctaCardText: {
    flex: 1,
  },
  ctaCardLabel: {
    letterSpacing: 0.88,
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  ctaSmall: {
    paddingVertical: 11,
    paddingHorizontal: 16,
    backgroundColor: '#FACF47',
    borderRadius: 32,
  },

  /* Footer */
  foot: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 16,
    flexWrap: 'wrap',
  },
  ghostButton: {
    backgroundColor: 'transparent',
    borderWidth: 1.5,
    borderColor: 'rgba(77, 63, 55, 0.18)',
    borderRadius: 32,
    paddingVertical: 11,
    paddingHorizontal: 18,
  },
  primaryButton: {
    backgroundColor: '#4B968D',
    borderRadius: 32,
    paddingVertical: 11,
    paddingHorizontal: 18,
  },
  help: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});
